import styles from "./Contact.module.css";
import {
  TextInput,
  SelectInput,
  Typography,
  Button,
  useToast,
} from "@grantyd/ui";
import { ReactComponent as InstagramIcon } from "@/assets/icons/instagram-white.svg";
import { ReactComponent as LinkedInIcon } from "@/assets/icons/linkedin-white.svg";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { contactData } from "@/data/contact";
import { logEvent } from "firebase/analytics";
import { analytics, functions } from "@/config/firebase";
import { httpsCallable } from "firebase/functions";
import { useState } from "react";

const Contact = () => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [contactComplete, setContactComplete] = useState<boolean>(false);
  const toast = useToast();

  const SuccessMessage = () => (
    <Typography
      as="h2"
      variant={isDesktop ? "display-md" : "headline-lg"}
      style={{
        fontWeight: "var(--font-weight-bold)",
        color: "var(--color-primary)",
      }}
    >
      Thanks for your interest, we'll be in touch soon!
    </Typography>
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { currentTarget } = e;
    const formData = new FormData(currentTarget);
    const formValues = Object.fromEntries(formData.entries());

    try {
      await httpsCallable(functions, "submitLeadForm")(formValues);
      setContactComplete(true);
      logEvent(analytics, "scheduled_consult");
    } catch (e) {
      toast.showError(<p>{e.message}</p>);
    }
  };

  return (
    <section className={styles.contactSection} id="contact">
      <div className={styles.container}>
        <div className={styles.content}>
          <Typography
            as="h2"
            variant={isDesktop ? "display-md" : "headline-lg"}
            style={{
              fontWeight: "var(--font-weight-bold)",
            }}
          >
            {contactData.heading}
          </Typography>
          <Typography as="p" variant="body-lg">
            {contactData.description}
          </Typography>
          <ul className={styles.bullets}>
            {contactData.bulletPoints.map((bullet, index) => (
              <li key={index}>
                <Typography as="span" variant="body-lg">
                  {bullet}
                </Typography>
              </li>
            ))}
          </ul>
          <div className={styles.contactInfo}>
            <Typography as="p" variant="label-lg">
              {contactData.contact.email}
            </Typography>
            <div className={styles.socialIcons}>
              {contactData.socialLinks.map((social) => (
                <a
                  key={social.platform}
                  href={social.url}
                  className={styles.icon}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.platform}
                >
                  {social.platform === "instagram" ? (
                    <InstagramIcon />
                  ) : (
                    <LinkedInIcon />
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.formCard}>
          {contactComplete ? (
            <SuccessMessage />
          ) : (
            <form
              className={styles.form}
              onSubmit={handleSubmit}
              autoComplete="on"
            >
              <div className={styles.row}>
                {contactData.form.fields.slice(0, 2).map((field) => (
                  <TextInput
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    required={field.required}
                    aria-label={field.name}
                    autoComplete={field.autoComplete}
                    minLength={field.minLength}
                  />
                ))}
              </div>
              {contactData.form.fields.slice(2, 5).map((field) => (
                <TextInput
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  required={field.required}
                  aria-label={field.name}
                  autoComplete={field.autoComplete}
                  type={field.type}
                  minLength={field.minLength}
                />
              ))}
              <SelectInput
                label={contactData.form.fields[5].label}
                name={contactData.form.fields[5].name}
                options={contactData.form.fields[5].options || []}
                aria-label={contactData.form.fields[5].name}
                required
              />
              <Button variant="primary" style={{ width: "100%" }}>
                {contactData.form.submitButton}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
